import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { concat, defer, from, Observable, of, ReplaySubject, Subscription, timer } from 'rxjs';
import { map, mergeMap, retry, switchMap } from 'rxjs/operators';
import { Unit, UnitsResponse } from '../../models/entities/unit';
import { Membership } from '../../models/entities/membership';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { MembersService } from '../members/members.service';
import { UnitsService } from './units.service';
import { Tag } from '../../models/entities/tag';
import { Routes } from '../../config/routes';

export type UnitsQuery = Parameters<UnitsService['getUnits']>[6];
export type UnitListState = { units: Unit[]; loaded: number; total: number; done: boolean; error?: HttpErrorResponse };
export type MemberListState = { members: Membership[]; done: boolean; error?: HttpErrorResponse };

type UnitEntry = { state$: ReplaySubject<UnitListState>; loadedAt: number | null; loading: boolean; orgId: number; query: UnitsQuery; sub?: Subscription };
type MemberEntry = { state$: ReplaySubject<MemberListState>; loadedAt: number | null; loading: boolean; sub?: Subscription };

export type PickerExtras = Record<string, string | string[] | null | undefined>;
export type PickerTag = { id: string; label: string; color?: string };
export type PickerUnit = {
    id: number;
    name: string;
    address?: { line1?: string; line2?: string; city?: string; region?: string; postalCode?: string };
    tags: string[];
    owners: { id?: number; name: string; email?: string; phone?: string; tags: string[]; emailInvalid?: boolean; href?: string }[];
    balance?: number;
    pastDue?: number;
    autopay?: boolean;
    deleted?: boolean;
    deletedOn?: string;
    thumbnail?: string | null;
    href?: string;
    extra?: PickerExtras;
    disabled?: boolean;
    disabledReason?: string;
};
/** How a page wants its units turned into the kit's shape. */
export type KitUnitOptions = {
    members?: Membership[];
    thumbnail?: boolean;
    disableUnit?: ((unit: Unit) => string | null) | null;
    unitExtras?: ((unit: Unit) => PickerExtras) | null;
};
export type KitUnits = { units: PickerUnit[]; unitTags: PickerTag[]; ownerTags: PickerTag[] };

/**
 * Units in the shape the kit's unit picker and unit table read, with the org's unit and
 * owner tags collected from them. One mapping for every page that shows units.
 */
export function toKitUnits(units: Unit[], options: KitUnitOptions = {}): KitUnits
{
    const unitTags = new Map<string, PickerTag>();
    const ownerTags = new Map<string, PickerTag>();
    const tagIds = (tags: Tag[] | undefined, into: Map<string, PickerTag>): string[] => (tags || [])
            .filter(t => !!t.tag)
            .map((t) => {
                into.set(t.tag, into.get(t.tag) || { id: t.tag, label: t.tag, color: t.taggableColor });

                return t.tag;
            });
    const byId = new Map((options.members || []).map(m => [m.id, m]));

    const mapped = units.map((u) => {
        const address = u.address;
        const reason = options.disableUnit ? options.disableUnit(u) : null;
        const extra = options.unitExtras ? options.unitExtras(u) : null;

        return {
            id: u.id,
            name: u.title || u.name || `Unit ${u.id}`,
            ...(address ? { address: { line1: address.line1, line2: address.line2, city: address.city, region: address.region, postalCode: address.postalCode } } : {}),
            tags: tagIds(u.tags, unitTags),
            ...(u.balance !== undefined && u.balance !== null ? { balance: u.balance } : {}),
            ...(u.pastDueBalance !== undefined && u.pastDueBalance !== null ? { pastDue: u.pastDueBalance } : {}),
            autopay: !!u.autopayEnabled || (u.owners || []).some(o => !!(byId.get(o.memberId) || o.membership)?.profile?.autopayEnabled),
            ...(u.deletedAt ? { deleted: true, deletedOn: u.deletedAt.format('MM/DD/YYYY h:mm a') } : {}),
            href: `/app/unit/detail/${u.id}`,
            ...(options.thumbnail ? { thumbnail: u.hasImages ? Routes.MakeLegFiCoreUrl(`/units/${u.id}/image?v=${u.imageVersion}`) : null } : {}),
            ...(extra ? { extra } : {}),
            ...(reason ? { disabled: true, disabledReason: reason } : {}),
            owners: (u.owners || []).map((o) => {
                const member = byId.get(o.memberId) || o.membership;
                // Core blanks `autogenerated_*@payhoa.com` placeholders but not the legacy `@legfi.com` ones.
                const email = member?.user?.hasAutogeneratedEmailAddress?.() ? null : member?.user?.email;

                return {
                    ...(o.memberId ? { id: o.memberId } : {}),
                    name: o.name || member?.name || 'Owner',
                    ...(email ? { email } : {}),
                    ...(member?.profile?.phone ? { phone: member.profile.phone } : {}),
                    tags: tagIds(member?.tags, ownerTags),
                    ...(o.hasInvalidEmailAddress ? { emailInvalid: true } : {}),
                    ...(o.memberId ? { href: `/app/members/${o.memberId}` } : {}),
                };
            }),
        };
    });

    return {
        units: mapped,
        unitTags: Array.from(unitTags.values()).sort((a, b) => a.label.localeCompare(b.label)),
        ownerTags: Array.from(ownerTags.values()).sort((a, b) => a.label.localeCompare(b.label)),
    };
}

export const UNIT_PAGE = 500;
/** Pages after the first load this many at a time; small pages put the first rows on screen sooner. */
const PARALLEL_PAGES = 4;
/** Older than this, a cached list still shows at once and reloads behind it, so balances stay current. */
export const STALE_MS = 5 * 60 * 1000;

/**
 * Every unit (and active member) of an org, loaded once and shared by every picker.
 * Keyed by org and query. An org switch drops it; a unit create, edit, delete,
 * restore or import reloads what open pickers are showing and drops the rest.
 */
@Injectable({ providedIn: 'root' })
export class UnitListService
{
    private _units = new Map<string, UnitEntry>();
    private _members = new Map<string, MemberEntry>();
    private _orgId: number | null = null;

    constructor(
            private _unitsService: UnitsService,
            private _membersService: MembersService,
    ) {
        this._unitsService.unitsChanged$?.subscribe(() => this.refresh());
    }

    /** Starts the org's unit (and member) load ahead of a picker opening. */
    warm(query: UnitsQuery = {}, withMembers = true): void {
        this.units$(query);
        if (withMembers) {
            this.members$();
        }
    }

    /** Lists an open picker is showing reload in place; the rest are dropped and load fresh next time. */
    refresh(): void {
        this._units.forEach((entry, key) => {
            if (!entry.state$.observed) {
                entry.sub?.unsubscribe();
                this._units.delete(key);
            } else if (!entry.loading) {
                this.loadUnits(entry, true);
            }
        });
        this._members.forEach((entry, key) => {
            if (!entry.state$.observed) {
                entry.sub?.unsubscribe();
                this._members.delete(key);
            } else if (!entry.loading) {
                this.loadMembers(entry);
            }
        });
    }

    /** Progress while loading, then the full list. Replays the latest state to late subscribers. */
    units$(query: UnitsQuery = {}, orgId: number | null = null): Observable<UnitListState> {
        const org = orgId || this.currentOrg();
        const key = `${org}|${this.stable(query)}`;
        const entry = this._units.get(key);
        if (entry) {
            if (!entry.loading && entry.loadedAt !== null && Date.now() - entry.loadedAt > STALE_MS) {
                this.loadUnits(entry, true);
            }

            return entry.state$.asObservable();
        }
        const fresh: UnitEntry = { state$: new ReplaySubject<UnitListState>(1), loadedAt: null, loading: false, orgId: org, query: query || {} };
        this._units.set(key, fresh);
        this.loadUnits(fresh, false);

        return fresh.state$.asObservable();
    }

    members$(): Observable<MemberListState> {
        const key = String(this.currentOrg());
        const entry = this._members.get(key);
        if (entry) {
            if (!entry.loading && entry.loadedAt !== null && Date.now() - entry.loadedAt > STALE_MS) {
                this.loadMembers(entry);
            }

            return entry.state$.asObservable();
        }
        const fresh: MemberEntry = { state$: new ReplaySubject<MemberListState>(1), loadedAt: null, loading: false };
        this._members.set(key, fresh);
        this.loadMembers(fresh);

        return fresh.state$.asObservable();
    }

    /** `quiet` reloads behind a list already shown: no progress, and a failure keeps the old list until the next stale window. */
    private loadUnits(entry: UnitEntry, quiet: boolean): void {
        const pages = new Map<number, Unit[]>();
        let units: Unit[] = [];
        let total = 0;
        entry.loading = true;
        entry.sub?.unsubscribe();
        entry.sub = this.fetch(entry.orgId, entry.query, 1).pipe(
                switchMap((first: UnitsResponse) => {
                    const count = (first.data || []).length === UNIT_PAGE ? Math.ceil((first.meta?.total ?? 0) / UNIT_PAGE) : 1;
                    const rest = Array.from({ length: Math.max(0, count - 1) }, (_, i) => i + 2);

                    return concat(
                            of({ page: 1, response: first }),
                            from(rest).pipe(mergeMap(page => this.fetch(entry.orgId, entry.query, page).pipe(map(response => ({ page, response }))), PARALLEL_PAGES)),
                    );
                }),
        ).subscribe({
            next: ({ page, response }: { page: number; response: UnitsResponse }) => {
                pages.set(page, response.data || []);
                units = [...pages.keys()].sort((a, b) => a - b).flatMap(p => pages.get(p));
                total = response.meta?.total ?? units.length;
                if (!quiet) {
                    entry.state$.next({ units, loaded: units.length, total, done: false });
                }
            },
            error: (err: HttpErrorResponse) => {
                entry.loading = false;
                if (quiet) {
                    entry.loadedAt = Date.now();
                    return;
                }
                entry.state$.next({ units, loaded: units.length, total: units.length, done: true, error: err });
                this.drop(this._units, entry);
            },
            complete: () => {
                entry.loading = false;
                entry.loadedAt = Date.now();
                entry.state$.next({ units, loaded: units.length, total: units.length, done: true });
            },
        });
    }

    private loadMembers(entry: MemberEntry): void {
        entry.loading = true;
        entry.sub?.unsubscribe();
        entry.sub = this._membersService.getMembers(null, { status: 'active', allTags: true, owners: true }).subscribe({
            next: (data: any) => {
                const members = (data?.memberships || [])
                        .map((m: any) => new Membership(m))
                        .sort((a: Membership, b: Membership) => (a.name || '').localeCompare(b.name || ''));
                entry.loading = false;
                entry.loadedAt = Date.now();
                entry.state$.next({ members, done: true });
            },
            error: (err: HttpErrorResponse) => {
                entry.loading = false;
                if (entry.loadedAt !== null) {
                    entry.loadedAt = Date.now();
                    return;
                }
                entry.state$.next({ members: [], done: true, error: err });
                this.drop(this._members, entry);
            },
        });
    }

    private drop<E>(map: Map<string, E>, entry: E): void {
        map.forEach((e, k) => {
            if (e === entry) {
                map.delete(k);
            }
        });
    }

    private currentOrg(): number {
        const orgId = LegFiJwtService.read()?.orgId ?? null;
        if (orgId !== this._orgId) {
            this._units.forEach(e => e.sub?.unsubscribe());
            this._members.forEach(e => e.sub?.unsubscribe());
            this._units.clear();
            this._members.clear();
            this._orgId = orgId;
        }

        return orgId;
    }

    private fetch(orgId: number, query: UnitsQuery, page: number): Observable<UnitsResponse> {
        return defer(() => this._unitsService.getUnits(orgId, '', 'name', 'asc', page, UNIT_PAGE, query || {})).pipe(
                retry({ count: 2, delay: (_err, attempt) => timer(attempt * 1000) }),
        );
    }

    private stable(query: UnitsQuery): string {
        const q = (query || {}) as Record<string, unknown>;
        const set = (v: unknown) => v !== undefined && v !== null && v !== false && !(Array.isArray(v) && !v.length);

        return JSON.stringify(Object.keys(q).sort().filter(k => set(q[k])).map(k => [k, q[k]]));
    }
}
